import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  setPersistence,
  browserLocalPersistence,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  getFirestore,
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';

// Configuração do Firebase via variáveis de ambiente
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Inicializar Firebase com proteção: falha não derruba o app (evita tela branca no mobile)
let app = null;
let auth = null;
let db = null;
export let firebaseInitFailed = false;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  setPersistence(auth, browserLocalPersistence)
    .catch(error => console.error('Erro ao configurar persistência:', error));
} catch (error) {
  firebaseInitFailed = true;
  console.error('Firebase init failed (app continua para evitar tela branca):', error);
}

export { app };
export { db };

// Provedor Google (só usado se auth existir)
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('profile');
googleProvider.addScope('email');
// Telefone da conta Google (requer People API ativa no projeto Google Cloud)
googleProvider.addScope('https://www.googleapis.com/auth/user.phonenumbers.read');

const STORAGE_KEY_PHONE = 'pcp_google_phone';

/** Guardar telefone da conta Google por uid (para usar após reload/onAuthChanged) */
export const setStoredGooglePhone = (uid, phone) => {
  if (!uid || typeof phone !== 'string') return;
  try {
    const key = `${STORAGE_KEY_PHONE}_${uid}`;
    localStorage.setItem(key, phone.trim());
  } catch (_) {}
};

/** Ler telefone guardado para o uid */
export const getStoredGooglePhone = (uid) => {
  if (!uid) return null;
  try {
    return localStorage.getItem(`${STORAGE_KEY_PHONE}_${uid}`) || null;
  } catch (_) {
    return null;
  }
};

/** Remover telefone guardado (ex.: no logout) */
export const clearStoredGooglePhone = (uid) => {
  if (!uid) return;
  try {
    localStorage.removeItem(`${STORAGE_KEY_PHONE}_${uid}`);
  } catch (_) {}
};

/**
 * Buscar telefone do perfil Google via People API (requer scope user.phonenumbers.read).
 * Retorna o primeiro número ou o preferido (mobile/home).
 */
const fetchGooglePhoneNumber = async (accessToken) => {
  if (!accessToken) return null;
  try {
    const params = new URLSearchParams({ personFields: 'phoneNumbers' });
    params.append('sources', 'READ_SOURCE_TYPE_PROFILE');
    params.append('sources', 'READ_SOURCE_TYPE_CONTACT');
    const res = await fetch(
      `https://people.googleapis.com/v1/people/me?${params}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return null;
    const list = data?.phoneNumbers ?? data?.phone_numbers ?? null;
    if (!Array.isArray(list) || list.length === 0) return null;
    const mobile = list.find((p) => p.formattedType === 'Mobile' || (p.type || '').toLowerCase() === 'mobile');
    const first = list[0];
    const chosen = mobile || first;
    return chosen?.value?.trim() || null;
  } catch {
    return null;
  }
};

/**
 * Fazer login com Google.
 * Quando possível, obtém também o telefone da conta Google (People API) e retorna em phoneNumber.
 */
export const loginWithGoogle = async () => {
  if (!auth) {
    throw new Error('Firebase não disponível. Verifique a conexão e o domínio autorizado no Console Firebase.');
  }
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    let phoneNumber = null;
    const credential = GoogleAuthProvider.credentialFromResult(result);
    if (credential?.accessToken) {
      phoneNumber = await fetchGooglePhoneNumber(credential.accessToken);
      if (phoneNumber) setStoredGooglePhone(user.uid, phoneNumber);
    }
    const finalPhone = phoneNumber ?? getStoredGooglePhone(user.uid) ?? null;

    return {
      uid: user.uid,
      email: user.email,
      name: user.displayName,
      photoURL: user.photoURL,
      phoneNumber: finalPhone,
      metadata: user.metadata,
    };
  } catch (error) {
    // Em ambientes que não suportam popup (webview mobile, MetaMask, etc),
    // fazer fallback para redirect, que é mais compatível.
    if (
      error?.code === 'auth/operation-not-supported-in-this-environment' ||
      error?.code === 'auth/popup-blocked' ||
      error?.code === 'auth/popup-closed-by-user'
    ) {
      try {
        await signInWithRedirect(auth, googleProvider);
        // Após o redirect, o fluxo continua em outra navegação;
        // o AuthContext usa onAuthStateChanged para refletir o login.
        return null;
      } catch (redirectError) {
        console.error('Erro ao fazer login com redirect:', redirectError);
        throw new Error(redirectError.message);
      }
    }

    console.error('Erro ao fazer login:', error);
    throw new Error(error.message);
  }
};

/**
 * Fazer logout
 */
export const logout = async () => {
  if (!auth) return;
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Erro ao fazer logout:', error);
    throw new Error(error.message);
  }
};

/**
 * Processar resultado de login por redirect (chamar no carregamento da app).
 * Se houver resultado, obtém o telefone da conta Google e guarda por uid.
 * Retorna o resultado do redirect (para o AuthContext atualizar estado se necessário).
 */
export const processRedirectResult = async () => {
  if (!auth) return null;
  try {
    const result = await getRedirectResult(auth);
    if (!result?.user) return null;
    const credential = GoogleAuthProvider.credentialFromResult(result);
    if (!credential?.accessToken) return result;
    const phoneNumber = await fetchGooglePhoneNumber(credential.accessToken);
    if (phoneNumber) setStoredGooglePhone(result.user.uid, phoneNumber);
    return result;
  } catch {
    return null;
  }
};

/**
 * Obter usuário atual
 */
export const getCurrentUser = () => {
  return auth?.currentUser ?? null;
};

/**
 * Observar mudanças de autenticação (se Firebase falhou na init, chama callback(null) e retorna no-op)
 */
export const onAuthChanged = (callback) => {
  if (!auth) {
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
};

// =====================
// Firestore Wallets API
// =====================

/**
 * Adicionar nova carteira para um usuário
 */
export const addWallet = async (userId, walletData) => {
  if (!db) throw new Error('Firebase não disponível.');
  try {
    const walletRef = await addDoc(collection(db, 'users', userId, 'wallets'), {
      ...walletData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return walletRef.id;
  } catch (error) {
    console.error('Erro ao adicionar carteira:', error);
    throw error;
  }
};

/**
 * Obter todas as carteiras de um usuário
 */
export const getUserWallets = async (userId) => {
  if (!db) return [];
  try {
    const walletsRef = collection(db, 'users', userId, 'wallets');
    const snapshot = await getDocs(walletsRef);
    
    const wallets = [];
    snapshot.forEach(doc => {
      wallets.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    
    return wallets;
  } catch (error) {
    console.error('Erro ao obter carteiras:', error);
    throw error;
  }
};

/**
 * Atualizar carteira
 */
export const updateWallet = async (userId, walletId, updates) => {
  if (!db) throw new Error('Firebase não disponível.');
  try {
    const walletRef = doc(db, 'users', userId, 'wallets', walletId);
    await updateDoc(walletRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Erro ao atualizar carteira:', error);
    throw error;
  }
};

/**
 * Deletar carteira
 */
export const deleteWallet = async (userId, walletId) => {
  if (!db) throw new Error('Firebase não disponível.');
  try {
    const walletRef = doc(db, 'users', userId, 'wallets', walletId);
    await deleteDoc(walletRef);
  } catch (error) {
    console.error('Erro ao deletar carteira:', error);
    throw error;
  }
};

export default auth; // pode ser null se init falhou
